import { NavLink, Outlet } from 'react-router-dom';
import { useSession } from '../auth/session';
import { ProfileMenu } from './ProfileMenu';
import { NotificationBell } from './NotificationBell';
import { PendingApprovals } from './PendingApprovals';
import { portalOptionLabel } from './portal-options';
import { NavMenu } from './NavMenu';
import { ADMIN_GROUPS, groupNav } from './nav-groups';

/**
 * The internal console's chrome. Retailers get ShopShell instead.
 *
 * RL-05 — the sections come from `me.pages`, which the server resolved from the
 * user's roles. This used to be a compiled array gated by permission; the list
 * is now data, so granting a role one more screen takes effect at next sign-in
 * rather than at the next release.
 *
 * `capability` is still applied here: a page can be granted to a role and still
 * not exist on this portal (D46), and a nav entry pointing at a market that does
 * not offer the thing reads as a broken product rather than an unfinished one.
 */
export function Shell() {
  const { me, portal, setPortalCode, signOut, offers } = useSession();
  if (!me || me.kind !== 'internal') return null;

  const items = me.pages
    .filter((p) => p.granted && p.sortOrder !== null)
    .filter((p) => !p.capability || offers(p.capability))
    .map((p) => ({ id: p.id, to: p.route, label: p.name }));

  return (
    <>
      <div className="mk-stripe" />
      <div className="mk-top">
        <div className="mk-top-in">
          <NavLink className="mk-wm" to="/dashboard">
            MIRAKI<i>ADMIN CONSOLE</i>
          </NavLink>
          <NavMenu entries={groupNav(items, ADMIN_GROUPS)} />
          <div className="mk-tools">
            {me.portals.length > 1 ? (
              <select
                className="mk-fld"
                style={{ width: 'auto', fontSize: 12 }}
                value={portal?.code ?? ''}
                onChange={(e) => setPortalCode(e.target.value)}
                aria-label="Portal"
              >
                {/*
                  * Two portals can carry the same NAME — the code is what is unique — and the
                  * switcher showed names only, so they rendered as two identical options with
                  * no way to tell which was which or to know you had picked the other one.
                  *
                  * The code is appended only where it disambiguates. Adding it everywhere would
                  * put "Australia Wholesale (aus)" in front of people who have one Australia and
                  * no question to answer.
                  */}
                {me.portals.map((p) => (
                  <option key={p.id} value={p.code}>{portalOptionLabel(p, me.portals)}</option>
                ))}
              </select>
            ) : (
              <span className="mk-hint">{portal?.name}</span>
            )}
            <NotificationBell base="internal" />
            <span className="mk-tools-sep" aria-hidden />
            <ProfileMenu
              name={me.name}
              email={me.email}
              subtitle={me.isSuperAdmin ? 'Super Admin' : `${me.permissions.length} permissions`}
              onSignOut={() => void signOut()}
            />
          </div>
        </div>
      </div>
      <main><PendingApprovals /><Outlet /></main>
    </>
  );
}
